import { FC, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPlateApi } from "../../http/carPlates/api";
import { CarPlateNoCarDepency } from "../../http/carPlates/types";
import { NestedPick, PaginationQueryWithFields } from "../../types";
import { downloadFileFromData, formatDateNumeric, isWithinLast48h } from "../../types/utils";
import Header from "../ui/Header";
import Panel from "../ui/Panel";
import Table from "../ui/Table";

import { useUserSettings } from "../../context/userSettingsContext";
import TextColumnFilter from "../ui/columnFilters/TextColumnFilter";
import CustomLink from "../ui/CustomLink";
import ExportButton from "../ui/ExportButton";
import { Action, getDeleteAction, getEditAction, getHistoryAction } from "../ui/ListItemMenu";
import NewItemIcon from "../ui/NewItemIcon";
import CarPlateHistoryDialog from "./CarPlateHistoryDialog";
import CreateCarPlateButton from "./CreateCarPlateButton";
import DeleteCarPlateDialog from "./DeleteCarPlateDialog";
import EditCarPlateDialog from "./EditCarPlateDialog";

const CAR_PLATE_FIELDS = [
  "id",
  "createdAt",
  "car.id",
  "car.vehicleIdentificationNumber",
  "car.carSubsidiary.subsidiary.id",
  "car.carSubsidiary.subsidiary.name",
  "startDate",
  "endDate",
  "nr",
  "versionNumber",
] as const;
type CarPlateField = (typeof CAR_PLATE_FIELDS)[number];
type CarPlateSearch = NestedPick<CarPlateNoCarDepency, CarPlateField>;

type CarPlatesProps = {
  onUpdate?: () => void;
};

const CarPlates: FC<CarPlatesProps> = ({ onUpdate }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const carPlatesApi = useCarPlateApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const [itemToDelete, setItemToDelete] = useState<CarPlateSearch>();
  const [itemToEdit, setItemToEdit] = useState<CarPlateSearch>();
  const [itemToShowVersions, setItemToShowVersions] = useState<CarPlateSearch>();
  const { id: carId } = useParams<{ id: string }>();

  const query: PaginationQueryWithFields<CarPlateNoCarDepency> = {
    ...filters.state,
    q: debouncedValue,
    fields: CAR_PLATE_FIELDS,
  };

  const result = carPlatesApi.useAdminCarPlates(query, aborter.current.signal, carId);
  const [data] = useDataInitializer(result.data, result.error);

  const filterOptions = {
    get: carPlatesApi.getFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const getRowActions = (item: CarPlateSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.carPlates.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    actions.push(getHistoryAction(i18n, () => setItemToShowVersions(item)));

    if (userSettings.permissions.carPlates.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  const content = (
    <>
      <Table<CarPlateSearch>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          main: userSettings.permissions.carPlates.insert && <CreateCarPlateButton carId={carId} onSubmit={onUpdate} />,
          secondary: (
            <ExportButton.Excel
              onClick={async () => {
                const r = await carPlatesApi.exportExcel(query, aborter.current.signal);
                r && downloadFileFromData(r?.data, "Car plates", r?.contentType);
              }}
            />
          ),
        }}
        head={
          <>
            <Table.Th title={i18n.t.common.number} fieldName="carPlate.number" order={orderOptions} />
            <Table.Th
              title={i18n.t.common.licensePlateValidityStart}
              fieldName="carPlate.startDate"
              order={orderOptions}
            />
            <Table.Th title={i18n.t.common.licensePlateValidityEnd} fieldName="carPlate.endDate" order={orderOptions} />
            {!carId && (
              <>
                <Table.Th
                  title={i18n.t.common.carVin}
                  fieldName="carPlate.car.vehicleIdentificationNumber"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
                <Table.Th
                  title={i18n.t.subsidiaries.singular}
                  fieldName="carPlate.car.subsidiary.id"
                  filters={filterOptions}
                  filter={TextColumnFilter}
                  order={orderOptions}
                />
              </>
            )}

            <th></th>
          </>
        }
        onRowClick={(item, event) => {
          event.preventDefault();
          setItemToEdit(item);
        }}
        renderRow={(item) => (
          <>
            <td>
              {isWithinLast48h(item.createdAt) && <NewItemIcon />}
              {item.nr}
            </td>
            <td>{!!item.startDate && formatDateNumeric(item.startDate, i18n.language)}</td>
            <td>{!!item.endDate && formatDateNumeric(item.endDate, i18n.language)}</td>
            {!carId && (
              <>
                <td>
                  {userSettings.permissions.cars.read ? (
                    <CustomLink to={`/admin/cars/${item.car.id}`}>{item.car.vehicleIdentificationNumber}</CustomLink>
                  ) : (
                    item.car.vehicleIdentificationNumber
                  )}
                </td>
                <td>
                  {item.car.carSubsidiary && (
                    <CustomLink to={`/admin/subsidiaries/${item.car.carSubsidiary.subsidiary.id}`}>
                      {item.car.carSubsidiary.subsidiary.name}
                    </CustomLink>
                  )}
                </td>
              </>
            )}
          </>
        )}
        rowActions={getRowActions}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {!!itemToDelete && (
        <DeleteCarPlateDialog
          carPlateId={itemToDelete.id}
          carId={itemToDelete.car.id}
          onClose={(success) => {
            setItemToDelete(undefined);
            success && onUpdate?.();
          }}
        />
      )}

      {!!itemToEdit && (
        <EditCarPlateDialog
          carPlateId={itemToEdit.id}
          onClose={(success) => {
            setItemToEdit(undefined);
            success && onUpdate?.();
          }}
          carId={carId}
        />
      )}

      {!!itemToShowVersions && (
        <CarPlateHistoryDialog
          itemToShowVersionsId={itemToShowVersions.id}
          itemVersionToShowVersions={itemToShowVersions.versionNumber}
          onClose={() => setItemToShowVersions(undefined)}
        />
      )}
    </>
  );

  return carId ? (
    content
  ) : (
    <>
      <Header title={i18n.t.carPlates.plural} />
      <Panel>{content}</Panel>
    </>
  );
};

export default CarPlates;
